import { useState } from 'react';
import { MessageSquare, Check, Loader2 } from 'lucide-react';

/**
 * StayInTheLoop — the public SMS opt-in for the "Utopian Creations Updates"
 * toll-free program (+1 877-831-0042).
 *
 * This form is a COMPLIANCE ARTIFACT, not just marketing UI. Carrier toll-free
 * verification requires that the opt-in shown in the submitted workflow
 * screenshot actually exist at a public URL a reviewer can reach — a program
 * whose documented consent path can't be found on the site gets rejected. Its
 * wording, field set, and unchecked-by-default checkbox mirror
 * `public/compliance/sms-opt-in-workflow.png` deliberately. If you change the
 * copy here, re-export that diagram to match, or the two disagree in review.
 *
 * Consent is recorded by our own platform: the submission posts to the hosted
 * signup endpoint, which writes `marketing_contacts` with `consent_at` +
 * `consent_source`, honours the suppression list (a number that texted STOP is
 * never resurrected by a web form), and enforces `consent_required`
 * server-side — the checkbox below is not the only thing standing between a
 * request and a consent record.
 */

// Public identifiers, not secrets — the slug and form id appear in the POST
// body of every submission. Hardcoded fallbacks on purpose: a missing env var
// must not silently break the one form a carrier reviewer is looking for.
const SIGNUP_SLUG = import.meta.env.VITE_SMS_SIGNUP_SLUG || 'workspace-ca80181a';
const SIGNUP_FORM_ID = import.meta.env.VITE_SMS_SIGNUP_FORM_ID || 'b7c41e28-9a05-4d63-8f21-3e6c0a95d4b7';
// In dev, '' lets Vite's /api/marketing proxy reach a local wrangler. In prod a
// missing var would otherwise post to this origin, hit the SPA fallback, and
// return a 200 that looks like success while storing nothing.
const API_BASE =
  import.meta.env.VITE_MARKETING_API_BASE ||
  (import.meta.env.DEV ? '' : 'https://utopian-marketing-api.jmiller-580.workers.dev');

export const SMS_CONSENT_TEXT =
  'I agree to receive recurring automated marketing text messages (offers, promotions & news) from ' +
  'Utopian Creations at the number provided. Consent is not a condition of purchase. Msg frequency ' +
  'varies. Msg & data rates may apply. Reply HELP for help, STOP to cancel.';

// Mirrors normalizePhone() in worker/marketing-api.js — reject here rather than
// let the worker silently drop an unparseable number as if it had subscribed.
const normalizePhone = (raw) => {
  const trimmed = (raw || '').trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return null;
  if (trimmed.startsWith('+')) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
};

const StayInTheLoop = ({ theme = 'light', className = '' }) => {
  const dark = theme === 'dark';
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false); // unchecked by default — required by the program
  const [website, setWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    const normalized = normalizePhone(phone);
    if (!normalized) {
      setError('Enter a valid mobile number, e.g. (555) 014-2288.');
      return;
    }
    if (!consent) {
      setError('Please check the consent box to subscribe.');
      return;
    }
    setError('');
    setStatus('sending');
    try {
      const body = new URLSearchParams({
        form_id: SIGNUP_FORM_ID,
        first_name: name.trim(),
        phone: normalized,
        consent: '1',
        website,
      });
      const res = await fetch(`${API_BASE}/api/marketing/signup/${SIGNUP_SLUG}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (!res.ok) throw new Error(`signup_failed_${res.status}`);
      // The worker always 303s to its ok page; a real failure is flagged on
      // that URL so we never show "you're on the list" over a dropped row.
      let err = '';
      try { err = new URL(res.url).searchParams.get('err') || ''; } catch { /* ignore */ }
      if (err) throw new Error(`signup_${err}`);
      setStatus('done');
    } catch {
      setStatus('error');
      setError('Something went wrong on our end. Text START to (877) 831-0042 to subscribe instead.');
    }
  };

  const label = dark ? 'text-gray-200' : 'text-gray-700';
  const input = dark
    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-purple-400'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-purple-500';
  const fine = dark ? 'text-gray-400' : 'text-gray-500';
  const link = dark ? 'text-purple-300 hover:text-purple-200' : 'text-purple-600 hover:text-purple-700';

  if (status === 'done') {
    return (
      <div className={`rounded-2xl border p-6 ${dark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'} ${className}`}>
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <Check className="h-4 w-4" />
          </span>
          <div>
            <h3 className={`text-lg font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>You&rsquo;re on the list!</h3>
            <p className={`mt-1 text-sm ${fine}`}>
              Thanks for subscribing to Utopian Creations Updates. You&rsquo;ll get a confirmation text from
              +1&nbsp;(877)&nbsp;831-0042. Msg frequency varies. Msg &amp; data rates may apply. Reply STOP any
              time to opt out, HELP for help.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border p-6 ${dark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white shadow-sm'} ${className}`}>
      <h3 className={`flex items-center gap-2 text-lg font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>
        <MessageSquare className={`h-5 w-5 ${dark ? 'text-purple-300' : 'text-purple-600'}`} />
        Stay in the loop
      </h3>
      <p className={`mt-1 text-sm ${fine}`}>Offers, promotions &amp; news from Utopian Creations.</p>

      <form onSubmit={submit} className="mt-4 space-y-3" noValidate>
        {/* Honeypot — bots fill it, humans never see it. */}
        <input
          type="text"
          name="website"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[5000px] h-0 w-0 opacity-0"
        />

        <div>
          <label htmlFor="sms-name" className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${label}`}>
            Name
          </label>
          <input
            id="sms-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="given-name"
            placeholder="Jane Smith"
            className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-purple-500 ${input}`}
          />
        </div>

        <div>
          <label htmlFor="sms-phone" className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${label}`}>
            Mobile number
          </label>
          <input
            id="sms-phone"
            type="tel"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            placeholder="(555) 014-2288"
            className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-purple-500 ${input}`}
          />
        </div>

        <label
          htmlFor="sms-consent"
          className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 text-xs leading-relaxed ${
            dark ? 'border-gray-700 bg-gray-900/40 text-gray-300' : 'border-purple-100 bg-purple-50/60 text-gray-600'
          }`}
        >
          <input
            id="sms-consent"
            type="checkbox"
            required
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-purple-600"
          />
          <span>
            I agree to receive recurring automated <strong>marketing</strong> text messages (offers, promotions
            &amp; news) from <strong>Utopian Creations</strong> at the number provided. Consent is not a condition
            of purchase. <strong>Msg frequency varies. Msg &amp; data rates may apply.</strong> Reply HELP for help,
            STOP to cancel.{' '}
            <a href="/privacy-policy#sms-privacy" className={link}>Privacy Policy</a> &amp;{' '}
            <a href="/terms-of-service#sms-terms" className={link}>Terms</a> apply.
          </span>
        </label>

        {error && (
          <p role="alert" className="text-xs font-medium text-red-500">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={status === 'sending'}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-purple-700 disabled:opacity-60"
        >
          {status === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {status === 'sending' ? 'Subscribing…' : 'Subscribe to updates'}
        </button>
      </form>

      <p className={`mt-4 border-t pt-3 text-xs ${dark ? 'border-gray-700' : 'border-gray-100'} ${fine}`}>
        Or text <strong>START</strong>, <strong>JOIN</strong>, or <strong>SUBSCRIBE</strong> to{' '}
        <a href="sms:+18778310042" className={link}>+1 (877) 831-0042</a>. See the full{' '}
        <a href="/sms-policy.html" className={link}>SMS Program Policy</a>.
      </p>
    </div>
  );
};

export default StayInTheLoop;
